import { Button } from "grabcad-ui-elements";
import {
    User,
    Role,
    LIST_USERS,
    CompanyAsAdmin,
    GET_COMPANY_AS_ADMIN,
    GET_COMPANY_AS_USER,
    GET_COMPANY_GLOBAL_ADMIN,
    GetCompanyQueryAsGLobalAdminResult,
    CompanyAsGlobalAdmin,
    GetCompanyQueryResult,
    GetCompanyQueryAsGlobalAdminVariables,
} from "../../graphql";
import { paneStyle, StyledTab } from "./companyAuthSetting/SsoStyledComponents";
import { IEditCompanyProps } from "./ConpanyInterface";
import {
    CompanyFormInput,
    RESYNC_COMPANY_ACLS,
    UPDATE_COMPANY,
    UPDATE_COMPANY_GLOBAL_ADMIN,
} from "../../graphql/Mutations/Companies";
import {
    GetCompanyLicensesQueryVariables,
    useCompanyLicensesQuery,
} from "../../graphql/Queries/Licenses/GetCompanyLicenses";
import { Notifier } from "../../view/Notifier";
import { alphabetizeByProp } from "../../utils/sort";
import { UserContext } from "../../components/User/UserProvider";
import { Breadcrumbs } from "../../view/Navigation/BreadCrumbs";
import { ApplicationContext } from "../../components/ApplicationProvider";
import { omitDeepTypenames } from "../../utils/omitTypenames";
import { FunctionComponent, useContext, useState, useEffect, ChangeEvent } from "react";
import { omit } from "lodash";
import { useLazyQuery, useMutation } from "@apollo/client";
import { InfoBanner } from "./companyAuthSetting/InfoBanner";
import { CompanyForm } from "./Company";
import ConfigureSSO from "./companyAuthSetting/ConfigureSSO";
import { useHistory, useLocation } from "react-router";
import DataPrivacy from "./DataPrivacy";
interface TabChangeEvent {
    activeIndex: number;
}

export const EditCompany: FunctionComponent<IEditCompanyProps> = (props) => {
    const userContext = useContext(UserContext);
    const { t } = useContext(ApplicationContext);
    const location = useLocation();
    const history = useHistory();

    const getTabFromUrl = () => {
        const params = new URLSearchParams(location.search);
        return Number(params.get("tab")) || 0;
    };

    const [activeTab, setActiveTab] = useState<number>(getTabFromUrl());
    const [resyncCompanyACLsResponse, setResyncCompanyACLResponse] = useState<{
        nTotal: number;
        nMissing: number;
        nExtra: number;
    }>();
    const [resyncCompanyACLsMutation] = useMutation(RESYNC_COMPANY_ACLS, {
        // eslint-disable-next-line @typescript-eslint/typedef
        update(cache, { data: { resyncCompanyACLs } }): void {
            setResyncCompanyACLResponse(resyncCompanyACLs);
        },
        onError: (error) => Notifier.error(error),
    });

    const variables: GetCompanyLicensesQueryVariables = {};
    const companyId = parseInt((props as any).match.params.companyId, 10);
    if (userContext.role === Role.GlobalAdmin) {
        variables.companyId = companyId;
    }
    const { companyLicenses } = useCompanyLicensesQuery({ variables });
    const userRole = userContext.role;

    // This seems like the least convoluted way to conditionally use one of 2 strongly typed queries.
    const lazyGlobalAdminQuery = useLazyQuery<
        GetCompanyQueryAsGLobalAdminResult,
        GetCompanyQueryAsGlobalAdminVariables
    >(GET_COMPANY_GLOBAL_ADMIN, { variables: { id: companyId } });
    const lazyAdminQuery = useLazyQuery<GetCompanyQueryResult>(GET_COMPANY_AS_ADMIN);
    const lazyUserQuery = useLazyQuery<GetCompanyQueryResult>(GET_COMPANY_AS_USER);

    useEffect(() => {
        if (userRole === Role.GlobalAdmin) {
            void lazyGlobalAdminQuery[0]();
        } else if (userRole === Role.CompanyAdmin) {
            void lazyAdminQuery[0]();
        } else {
            void lazyUserQuery[0]();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Unclear why TS can't properly infer this type
    const company: CompanyAsAdmin | CompanyAsGlobalAdmin | undefined =
        lazyGlobalAdminQuery[1].data?.company ||
        lazyAdminQuery[1].data?.company ||
        lazyUserQuery[1].data?.company;

    if (!company) {
        return <div className="ui loader active" />;
    }

    const companyInput: CompanyFormInput = omit(omitDeepTypenames(company), [
        "admins", // Queried data returns User objects, but mutation variables input is a flat list of emails, added below.
        "accessOptions",
    ]) as Omit<CompanyAsAdmin | CompanyAsGlobalAdmin, "admins" | "accessOptions">;
    companyInput.admins = [...company.admins]
        .sort(alphabetizeByProp("email"))
        .map((admin: User) => admin.email);

    const CompanyBasic = () => {
        return (
            <div>
                <CompanyForm
                    companyInput={companyInput}
                    mutation={
                        userRole === Role.GlobalAdmin ? UPDATE_COMPANY_GLOBAL_ADMIN : UPDATE_COMPANY
                    }
                    refetchQueries={[{ query: LIST_USERS }]}
                    editing={true}
                    readOnly={userRole === Role.User}
                    licenses={companyLicenses?.licenses}
                    accessOptions={(company as CompanyAsGlobalAdmin).accessOptions}
                    {...props}
                />

                {userRole === Role.GlobalAdmin && (
                    <>
                        <hr />
                        <Button
                            id="qa-company-resync-acls"
                            type="button"
                            secondary
                            disabled={false}
                            className="primary left floated"
                            onClick={() => resyncCompanyACLsMutation({ variables: { companyId } })}
                        >
                            Resync ACLs
                        </Button>
                        {resyncCompanyACLsResponse && (
                            <div style={{ display: "inline-block" }}>
                                <div id="qa-resync-acl-total">{`Total number of printer / user ACL pairs: ${resyncCompanyACLsResponse.nTotal}`}</div>
                                <div id="qa-resync-acl-extra">{`Number extra found in cloud db: ${resyncCompanyACLsResponse.nExtra}`}</div>
                                <div id="qa-resync-acl-missing">{`Number missing on cloud db: ${resyncCompanyACLsResponse.nMissing}`}</div>
                            </div>
                        )}
                    </>
                )}
            </div>
        );
    };

    const handleTabChange = (e: ChangeEvent<HTMLInputElement>, { activeIndex }: TabChangeEvent) => {
        setActiveTab(activeIndex);

        const params = new URLSearchParams(location.search);
        params.set("tab", String(activeIndex));

        history.replace({
            search: params.toString(),
        });

        // SSO tab index shifts based on whether the company is internal (+1 for Data Privacy tab)
        const ssoTabIndex = company?.internal ? 2 : 1;
        if (activeIndex === ssoTabIndex) {
            if (userRole === Role.CompanyAdmin) {
                void lazyAdminQuery[0]();
            } else if (userRole !== Role.GlobalAdmin) {
                void lazyUserQuery[0]();
            }
        }
    };

    const panes = [
        {
            menuItem: t("company.basic"),
            render: () => CompanyBasic(),
            pane: { style: paneStyle },
        },

        //Add only if company is internal
        ...(company?.internal
            ? [
                  {
                      menuItem: {
                          content: t("company.data_privacy"),
                      },
                      render: () => (
                          <div>
                              <DataPrivacy />
                          </div>
                      ),
                      pane: { style: paneStyle },
                  },
              ]
            : []),

        {
            menuItem: {
                content: t("company.sso_settings"),
                disabled: !company?.eligibleForSSO,
            },
            render: () => (
                <div>
                    <ConfigureSSO authSettings={company?.authSettings} />
                </div>
            ),
            pane: { style: paneStyle },
        },
    ];

    return (
        <>
            <Breadcrumbs sections={[{ label: t("company.breadcrumb") }]} />
            <br />
            {userRole === Role.GlobalAdmin ? (
                CompanyBasic()
            ) : (
                <>
                    {!company?.eligibleForSSO && <InfoBanner />}
                    <StyledTab
                        menu={{ secondary: true, pointing: true, "data-testid": "tabs-menu" }}
                        panes={panes}
                        activeIndex={activeTab}
                        onTabChange={handleTabChange}
                    />
                </>
            )}
        </>
    );
};
